import React, { useMemo } from 'react';
import Modal from '@amzn/awsui-components-react/polaris/modal';
import Box from '@amzn/awsui-components-react/polaris/box';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Button from '@amzn/awsui-components-react/polaris/button';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import { useTranslation } from 'react-i18next';
import { BulkLeaveWaitlistCommandOutput, WaitlistEntry } from '@amzn/red-velvet-api';
import { useBulkLeaveWaitlist } from '../../hooks/waitlist/useBulkLeaveWaitlist';
import { getVolunteerFullName, groupEntriesByShift } from '../../utils/waitlistUtils';
import { publishKatalMetric } from '../katalAnalytics';
import {
  COORDINATOR_WAITLIST_REMOVE_FROM_WAITLIST,
  COORDINATOR_WAITLIST_REMOVE_FROM_WAITLIST_BULK,
} from '../../utils/constants';

export interface RemoveWaitlistsModalProps {
  /** Whether the modal is shown. */
  visible: boolean;
  /**
   * The waitlist entries to remove. A single entry renders the single-remove
   * confirmation message; multiple entries render the bulk message.
   */
  entries: WaitlistEntry[];
  /**
   * Called when the user dismisses the modal without confirming, or to close it after a
   * whole-request error. No server change has occurred in these cases, so the caller need
   * not refetch here.
   */
  onDismiss: () => void;
  /**
   * Called once the bulk-leave request returns, whether every entry was removed or only some.
   * The caller closes the modal, clears selection, surfaces the per-item result (succeeded /
   * failed counts) in a page-level flashbar, and refetches the waitlist.
   */
  onComplete: (result: BulkLeaveWaitlistCommandOutput) => void;
}

export function RemoveWaitlistsModal({
  visible,
  entries,
  onDismiss,
  onComplete,
}: RemoveWaitlistsModalProps) {
  const { t } = useTranslation('coordinatorManageWaitlist');
  const { mutate, isPending, isError, error, reset } = useBulkLeaveWaitlist();

  const message = useMemo(() => {
    if (entries.length === 1) {
      return t('removeWaitlistsModal.messageSingle', { name: getVolunteerFullName(entries[0]) });
    }
    return t('removeWaitlistsModal.messageBulk', { count: entries.length });
  }, [entries, t]);

  const handleDismiss = () => {
    if (isPending) return;
    reset();
    onDismiss();
  };

  const handleConfirm = () => {
    publishKatalMetric(
      entries.length === 1
        ? COORDINATOR_WAITLIST_REMOVE_FROM_WAITLIST
        : COORDINATOR_WAITLIST_REMOVE_FROM_WAITLIST_BULK,
      ''
    );
    reset();
    mutate(
      { entries: groupEntriesByShift(entries) },
      {
        // Fires for both full and partial success; the parent surfaces the per-item result
        // (succeeded / failed counts) in a page-level flashbar and refetches.
        onSuccess: (result) => onComplete(result),
      }
    );
  };

  return (
    <Modal
      data-testid="remove-waitlist-modal"
      visible={visible}
      onDismiss={handleDismiss}
      header={t('removeWaitlistsModal.title')}
      closeAriaLabel={t('removeWaitlistsModal.close')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={isPending}>
              {t('removeWaitlistsModal.dismiss')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={isPending}
              disabled={entries.length === 0}
            >
              {t('removeWaitlistsModal.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="s">
        {message}
        {isError && <Alert type="error">{error?.message || t('removeWaitlistsModal.error')}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
